import React from 'react';
import * as Select from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { uiStore } from '@/stores/UIStore';
import { APP_LOCALES, LOCALE_LABELS, type AppLocale } from '@/i18n';

import '@/styles/form-select.css';
import './language-selector.css';

export interface LanguageSelectorProps {
  id?: string;
  /** When set, controls a local value (e.g. invite email language) instead of app preference */
  value?: AppLocale;
  onValueChange?: (locale: AppLocale) => void;
  className?: string;
  /** Visually hide the label (still available to screen readers) */
  hideLabel?: boolean;
  size?: 'default' | 'compact';
}

/**
 * Locale select. Default mode writes UIStore preferred language (localStorage + i18n).
 * Controlled mode (`value` + `onValueChange`) is for per-invite email language.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = observer(
  ({ id = 'language-selector', value, onValueChange, className, hideLabel = false, size = 'default' }) => {
    const { t } = useTranslation('language');
    const controlled = value != null && onValueChange != null;
    const current = controlled ? value : uiStore.preferredLanguage;

    const handleChange = (next: string) => {
      const locale = next as AppLocale;
      if (controlled) {
        onValueChange(locale);
        return;
      }
      void uiStore.setPreferredLanguage(locale);
    };

    return (
      <div className={`language-selector language-selector--${size}${className ? ` ${className}` : ''}`}>
        <label htmlFor={id} className={hideLabel ? 'sr-only' : 'language-selector__label'}>
          {t('language')}
        </label>
        <Select.Root value={current} onValueChange={handleChange}>
          <Select.Trigger id={id} className="form-select-trigger language-selector__trigger" aria-label={t('language')}>
            <Select.Value />
            <Select.Icon className="form-select-icon">
              <ChevronDown size={16} aria-hidden />
            </Select.Icon>
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className="form-select-content" position="popper" sideOffset={4}>
              <Select.Viewport>
                {APP_LOCALES.map((locale) => (
                  <Select.Item key={locale} value={locale} className="form-select-item">
                    <Select.ItemText>{LOCALE_LABELS[locale]}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>
    );
  }
);

export default LanguageSelector;
